<template>
  <ClientOnly>
    <div class="war-room-exposed-comments" :class="commentsClasses">
      <NuxtLink
        v-for="comment in comments"
        :key="comment.id"
        :to="comment.url"
        class="war-room-exposed-comments__comment"
        @click.native="onClick(comment.id)"
      >
        <div class="war-room-exposed-comments__col">
          <WarRoomAvatar type="next-tiny" :name="comment.user.username" :src="comment.user.avatar_url" />
        </div>
        <div class="war-room-exposed-comments__col">
          <b>{{ comment.user.username }}:</b>
          <span v-html="comment.text"></span>
        </div>
      </NuxtLink>
    </div>
  </ClientOnly>
</template>

<script lang="ts">
import type { PropOptions } from 'vue';
import Vue from 'vue';
import { buildCommentUrl, prepareCommentText } from './utils';
import type { IPost, IPostComment } from '@/types/community';
import WarRoomAvatar from '@/components/partials/war-room/WarRoomAvatar.vue';
import { sortByFieldDesc } from '~/components/utils/sorting_func';

export default Vue.extend({
  name: 'WarRoomExposedComments',
  components: {
    WarRoomAvatar,
  },
  props: {
    post: {
      type: Object,
      required: true,
    } as PropOptions<IPost>,
  },
  computed: {
    comments(): IPostComment[] {
      return Object.values(this.post.popular_comments?.comment_dict || {})
        .sort(sortByFieldDesc('rating'))
        .map((comment: IPostComment) => {
          return {
            ...comment,
            url: this.getCommentUrl(comment.id),
            user: this.post.popular_comments?.user_dict[comment.user_username] || {},
            text: this.getCommentText(comment.text || ''),
          };
        });
    },
    commentsClasses(): Record<string, boolean> {
      return {
        'war-room-exposed-comments--disabled': !this.$auth.loggedIn,
        'war-room-exposed-comments--pinned': this.post.pinned,
      };
    },
  },
  methods: {
    getCommentUrl(commentId: number): string {
      return buildCommentUrl(this.post.slug, commentId);
    },
    getCommentText(text = ''): string {
      return prepareCommentText(text);
    },
    onClick(commentId: number): void {
      this.$analytics.logEvent(`war-room__post-preview__exposed-comment__click`, {
        postId: this.post.id,
        commentId,
      });
    },
  },
});
</script>

<style scoped lang="scss">
.war-room-exposed-comments {
  margin-top: 10px;
  padding: 12px;
  background: var(--theme-color-over-card);
  border-radius: 4px;
  flex-grow: 1;
  position: relative;
  z-index: 2;

  &--pinned {
    background: rgba(0, 0, 0, 0.05);
  }

  &--disabled {
    pointer-events: none;
  }

  &__comment {
    text-decoration: none;
    display: flex;

    & + & {
      margin-top: 8px;
    }
  }

  &__col {
    @include subhead;
    @include multiline-overflow;

    color: var(--theme-color-text);

    &:first-child {
      flex-shrink: 0;
    }

    & + & {
      margin-left: 6px;
    }
  }
}
</style>
